import { login as authLogin, logout as authLogout, refresh as authRefresh, verifyToken } from './authClient';
import { clearTokens, getStoredTokens, isTokenExpired, storeTokens } from './tokenStorage';
import type { AuthenticatedUser, Role, StoredTokens } from './types';

// In-tab dedup — concurrent callers share one in-flight refresh.
let refreshPromise: Promise<StoredTokens | null> | null = null;

export async function signIn(loginName: string, password: string): Promise<AuthenticatedUser | null> {
  const tokens = await authLogin(loginName, password);
  storeTokens(tokens);
  return decodeUser(tokens.idToken);
}

export async function signOut(): Promise<void> {
  const tokens = getStoredTokens();
  try {
    await authLogout(tokens?.accessToken ?? null, tokens?.refreshToken ?? null);
  } finally {
    clearTokens();
  }
}

/** Silent refresh. Returns null (and clears the session) when not possible. */
export async function refreshAccessToken(): Promise<StoredTokens | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doRefresh(): Promise<StoredTokens | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;
  if (!tokens.refreshToken) {
    // Local auth mode issues no refresh tokens: once expired, log in again.
    clearTokens();
    return null;
  }
  try {
    storeTokens(await authRefresh(tokens.refreshToken));
    return getStoredTokens();
  } catch {
    // Don't clear a newer session that replaced this one mid-flight.
    if (getStoredTokens()?.refreshToken === tokens.refreshToken) clearTokens();
    return null;
  }
}

/** Cold-load check: confirm the stored token with the backend, refreshing first if expired. */
export async function getAuthenticatedUser(): Promise<AuthenticatedUser | null> {
  let tokens = getStoredTokens();
  if (!tokens) return null;

  if (isTokenExpired()) {
    tokens = await refreshAccessToken();
    if (!tokens) return null;
  }

  const verify = await verifyToken(tokens.accessToken);
  if (!verify.valid) {
    if (getStoredTokens()?.accessToken === tokens.accessToken) clearTokens();
    return null;
  }

  const user = decodeUser(tokens.idToken) ?? (verify.claims ? claimsToUser(verify.claims) : null);
  if (user && verify.claims) {
    // Server-validated claims are the authoritative role source.
    const roles = rolesFromClaims(verify.claims);
    if (roles.length > 0) user.roles = roles;
  }
  return user;
}

function decodeUser(token: string): AuthenticatedUser | null {
  const claims = decodeJwtPayload(token);
  return claims ? claimsToUser(claims) : null;
}

function claimsToUser(claims: Record<string, unknown>): AuthenticatedUser {
  const email = (claims['email'] as string) ?? '';
  return {
    userId: (claims['sub'] as string) ?? '',
    email,
    name: (claims['name'] as string) ?? (claims['preferred_username'] as string) ?? email,
    roles: rolesFromClaims(claims),
  };
}

function isRole(r: unknown): r is Role {
  return r === 'Admin' || r === 'User';
}

/**
 * Local mode: a plain `role` claim (string or array). Zitadel mode: project
 * roles as an object keyed by role, under the generic or project-scoped claim,
 * sometimes JSON-encoded as a string. Mirrors the backend role flattening.
 */
function rolesFromClaims(claims: Record<string, unknown>): Role[] {
  const found = new Set<Role>();
  const plain = claims['role'];
  for (const r of Array.isArray(plain) ? plain : [plain]) if (isRole(r)) found.add(r);

  for (const [key, value] of Object.entries(claims)) {
    if (key !== 'urn:zitadel:iam:org:project:roles' && !/^urn:zitadel:iam:org:project:[^:]+:roles$/.test(key)) continue;
    let parsed: unknown = value;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        continue;
      }
    }
    if (parsed && typeof parsed === 'object') Object.keys(parsed).filter(isRole).forEach((r) => found.add(r));
  }
  return Array.from(found);
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}
